/**
 * PeerReviewForm — Review fill form for an employee (reviewer role).
 *
 * Topics rendered as clean cards; Likert questions use toggle-button ratings;
 * Yes/No questions use Yes/No buttons. One optional overall comment box at
 * the end (TipTap rich-text editor). No per-question comment boxes.
 *
 * "Save Draft" persists progress without finalising.
 * "Submit" asks for confirmation then permanently submits.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { fetchData, postData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import {
  myPeerReviewsUrl,
  submitPeerReviewUrl,
  peerReviewTemplatesUrl,
} from "../../../Service/Apis";
import {
  PeerReviewAnswer,
  PeerReviewAnswerFormData,
  PeerReviewAssignment,
  PeerReviewQuestion,
  PeerReviewResponse,
  PeerReviewTemplate,
  PeerReviewTopic,
} from "./Types/PeerReviewTypes";
import SkillDocumentCommentBox from "../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";

// ── helpers ─────────────────────────────────────────────────────────────────

const daysUntil = (dateStr: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil(
    (new Date(dateStr).getTime() - today.getTime()) / 86_400_000
  );
};

const initials = (name?: string) =>
  (name ?? "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

/** Coloured left-border accent per topic index */
const TOPIC_COLORS = [
  "#1976d2",
  "#388e3c",
  "#f57c00",
  "#7b1fa2",
  "#c62828",
  "#00838f",
];

// ── LikertButtons ─────────────────────────────────────────────────────────────

const LikertButtons: React.FC<{
  scale: number;
  value: number | null;
  onChange: (v: number) => void;
  disabled: boolean;
}> = ({ scale, value, onChange, disabled }) => (
  <Box display="flex" gap={0.75} flexWrap="wrap">
    {Array.from({ length: scale }, (_, i) => i + 1).map((n) => {
      const selected = value === n;
      return (
        <Tooltip key={n} title={n === 1 ? "Poor" : n === scale ? "Excellent" : ""}>
          <Box
            component="button"
            onClick={() => !disabled && onChange(n)}
            disabled={disabled}
            sx={{
              width: 40,
              height: 40,
              border: selected ? "2px solid" : "1.5px solid",
              borderColor: selected ? "primary.main" : "grey.400",
              borderRadius: "50%",
              background: selected ? "primary.main" : "transparent",
              backgroundColor: selected ? "primary.main" : "transparent",
              color: selected ? "#fff" : "text.primary",
              fontWeight: selected ? 700 : 400,
              fontSize: 14,
              cursor: disabled ? "default" : "pointer",
              transition: "all 0.15s",
              "&:hover:not(:disabled)": {
                borderColor: "primary.main",
                backgroundColor: selected ? "primary.dark" : "primary.50",
                color: selected ? "#fff" : "primary.main",
              },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            {n}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

// ── YesNoButtons ────────────────────────────────────────────────────────────

const YesNoButtons: React.FC<{
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled: boolean;
}> = ({ value, onChange, disabled }) => (
  <Box display="flex" gap={1}>
    {[
      { label: "Yes", val: true, color: "#2e7d32" },
      { label: "No", val: false, color: "#c62828" },
    ].map(({ label, val, color }) => {
      const selected = value === val;
      return (
        <Box
          key={label}
          component="button"
          onClick={() => !disabled && onChange(val)}
          disabled={disabled}
          sx={{
            px: 3,
            py: 0.75,
            border: "1.5px solid",
            borderColor: selected ? color : "grey.400",
            borderRadius: 2,
            background: selected ? color : "transparent",
            color: selected ? "#fff" : "text.primary",
            fontWeight: selected ? 700 : 400,
            fontSize: 14,
            cursor: disabled ? "default" : "pointer",
            transition: "all 0.15s",
            "&:hover:not(:disabled)": {
              borderColor: color,
              backgroundColor: selected ? color : `${color}15`,
              color: selected ? "#fff" : color,
            },
            "&:disabled": { opacity: 0.5 },
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
);

// ── Main component ───────────────────────────────────────────────────────────

const PeerReviewForm: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState<PeerReviewAssignment | null>(null);
  const [template, setTemplate] = useState<PeerReviewTemplate | null>(null);
  const [existingResponse, setExistingResponse] = useState<PeerReviewResponse | null>(null);

  const [answers, setAnswers] = useState<Record<string, PeerReviewAnswerFormData>>({});
  const [overallComment, setOverallComment] = useState("");

  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!assignmentId) return;
    setLoading(true);
    dispatch(fetchData(`${myPeerReviewsUrl}${assignmentId}/`))
      .then((res: any) => {
        if (!res) return;
        setAssignment(res);

        const cycleId = res.cycle;
        return dispatch(
          fetchData(`${peerReviewTemplatesUrl}?cycle=${cycleId}`)
        ).then((tRes: any) => {
          const tmpl: PeerReviewTemplate | null =
            tRes?.results?.[0] ?? tRes?.[0] ?? null;
          setTemplate(tmpl);

          if (res.response) {
            const resp: PeerReviewResponse = res.response;
            setExistingResponse(resp);
            setOverallComment(resp.overall_comment ?? "");
            const pre: Record<string, PeerReviewAnswerFormData> = {};
            resp.answers.forEach((ans: PeerReviewAnswer) => {
              pre[ans.question] = {
                question: ans.question,
                likert_rating: ans.likert_rating,
                yes_no_answer: ans.yes_no_answer,
              };
            });
            setAnswers(pre);
          }
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  useEffect(() => {
    const onScroll = () => {
      const el = headerRef.current;
      if (!el) return;
      setScrolled(el.getBoundingClientRect().bottom < 64);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const buildPayload = (isDraft: boolean) => ({
    is_draft: isDraft,
    overall_comment: overallComment,
    answers: Object.values(answers),
  });

  const handleSaveDraft = async () => {
    if (!assignmentId) return;
    setSavingDraft(true);
    try {
      await dispatch(postData(submitPeerReviewUrl(assignmentId), buildPayload(true)));
      showToast("Draft saved.", "success", false);
    } catch {
      showToast("Failed to save draft.", "error", false);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (!assignmentId) return;
    setSubmitting(true);
    setConfirmOpen(false);
    try {
      await dispatch(postData(submitPeerReviewUrl(assignmentId), buildPayload(false)));
      showToast("Review submitted successfully.", "success", false);
      navigate("/pms/peer-review/my-reviews");
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ??
        err?.response?.data?.non_field_errors?.[0] ??
        "Failed to submit. Please try again.";
      showToast(detail, "error", false);
    } finally {
      setSubmitting(false);
    }
  };

  const setLikertRating = (questionId: string, rating: number) =>
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        likert_rating: rating,
        yes_no_answer: null,
      },
    }));

  const setYesNo = (questionId: string, value: boolean) =>
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        likert_rating: null,
        yes_no_answer: value,
      },
    }));

  // ── loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Assignment not found or access denied.</Alert>
      </Container>
    );
  }

  const cycleStatus = assignment.cycle_status;
  const isSubmitted = assignment.status === "submitted";
  const isExpired = assignment.status === "expired";
  const daysLeft = assignment.cycle_end_date ? daysUntil(assignment.cycle_end_date) : null;
  const isPastDeadline = daysLeft !== null && daysLeft < 0;
  const isReadOnly = isSubmitted || isExpired || cycleStatus === "closed" || isPastDeadline;

  const totalQuestions =
    template?.topics.reduce((acc, t) => acc + t.questions.length, 0) ?? 0;
  const answeredCount = Object.values(answers).filter(
    (a) => a.likert_rating !== null || a.yes_no_answer !== null
  ).length;
  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  return (
    <>
      {/* ── Fixed compact header — slides in when user scrolls past the full header ── */}
      <Box
        sx={{
          position: "fixed",
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1050,
          transform: scrolled ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: "linear-gradient(135deg, #145A32 0%, #1E7B44 70%, #9DC4AD 100%)",
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.3)" : "none",
          pointerEvents: scrolled ? "auto" : "none",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => navigate("/pms/peer-review/my-reviews")}
              sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                border: "1.5px solid rgba(255,255,255,0.4)",
              }}
            >
              {initials(assignment.reviewee_name)}
            </Avatar>

            <Box flex={1} sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} color="#fff" noWrap>
                {assignment.reviewee_name}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.65)", ml: 1 }}
                >
                  · {assignment.cycle_name}
                </Typography>
              </Typography>
              {totalQuestions > 0 && (
                <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.25)",
                      "& .MuiLinearProgress-bar": { bgcolor: "#fff", borderRadius: 3 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    {answeredCount}/{totalQuestions} ({progress}%)
                  </Typography>
                </Box>
              )}
            </Box>

            <Chip
              label={assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
              size="small"
              sx={{
                bgcolor: isSubmitted
                  ? "rgba(76,175,80,0.25)"
                  : isExpired
                  ? "rgba(244,67,54,0.25)"
                  : "rgba(255,255,255,0.15)",
                color: "#fff",
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: 11,
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 10, mb: 10 }}>

      {/* ── Full header card — stays in document flow, acts as scroll sentinel ─ */}
      <div ref={headerRef}>
      <Card
        elevation={4}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #145A32 0%, #1E7B44 70%, #9DC4AD 100%)",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <IconButton
            onClick={() => navigate("/pms/peer-review/my-reviews")}
            size="small"
            sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 18,
              border: "2px solid rgba(255,255,255,0.4)",
            }}
          >
            {initials(assignment.reviewee_name)}
          </Avatar>

          <Box flex={1}>
            <Typography variant="h6" fontWeight={700} color="#fff">
              {assignment.reviewee_name ?? "Peer Review"}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
              {assignment.cycle_name}
              {assignment.cycle_end_date && ` · Deadline: ${assignment.cycle_end_date}`}
            </Typography>
          </Box>

          <Chip
            label={assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
            size="small"
            sx={{
              bgcolor: isSubmitted
                ? "rgba(76,175,80,0.25)"
                : isExpired
                ? "rgba(244,67,54,0.25)"
                : "rgba(255,255,255,0.15)",
              color: "#fff",
              fontWeight: 600,
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
        </Box>

        {/* Progress bar */}
        {totalQuestions > 0 && (
          <Box sx={{ px: 3, py: 1.5, bgcolor: "#f0f7f3" }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.primary" fontWeight={500}>
                Completion
              </Typography>
              <Typography variant="caption" sx={{ color: "#145A32" }} fontWeight={700}>
                {answeredCount} / {totalQuestions} &nbsp;({progress}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 7,
                borderRadius: 4,
                bgcolor: "#c8e0d0",
                "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#1E7B44" },
              }}
            />
          </Box>
        )}
      </Card>
      </div>

      {/* ── Status banners ────────────────────────────────────────────────── */}
      {isSubmitted && (
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
        >
          You have already submitted this review. It cannot be modified.
        </Alert>
      )}
      {isExpired && (
        <Alert
          icon={<ErrorOutlineIcon />}
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
        >
          This assignment has expired.
        </Alert>
      )}
      {cycleStatus === "closed" && !isSubmitted && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          This review cycle has been closed. No submissions are accepted.
        </Alert>
      )}
      {isPastDeadline && !isSubmitted && cycleStatus !== "closed" && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          The deadline for this cycle has passed.
        </Alert>
      )}
      {!isReadOnly && daysLeft !== null && daysLeft <= 3 && (
        <Alert
          icon={<WarningAmberIcon />}
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Deadline in <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong>. Make sure to submit in time.
        </Alert>
      )}

      {/* ── No template ───────────────────────────────────────────────────── */}
      {!template ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No questionnaire template found for this cycle. Contact HR.
        </Alert>
      ) : (
        <>
          {/* ── Topics ──────────────────────────────────────────────────── */}
          {template.topics.map((topic: PeerReviewTopic, ti: number) => {
            const accentColor = TOPIC_COLORS[ti % TOPIC_COLORS.length];
            const answeredInTopic = topic.questions.filter((q) => {
              const a = answers[q.id];
              return a && (a.likert_rating !== null || a.yes_no_answer !== null);
            }).length;
            const allAnswered = answeredInTopic === topic.questions.length;

            return (
              <Paper
                key={topic.id}
                elevation={2}
                sx={{
                  mb: 2.5,
                  borderRadius: 3,
                  overflow: "hidden",
                  borderLeft: `4px solid ${accentColor}`,
                }}
              >
                {/* Topic header */}
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: `${accentColor}08`,
                    borderBottom: "1px solid",
                    borderColor: "grey.100",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: accentColor }}
                  >
                    {topic.topic_name}
                  </Typography>
                  <Chip
                    label={`${answeredInTopic} / ${topic.questions.length}`}
                    size="small"
                    icon={allAnswered ? <CheckCircleIcon fontSize="small" /> : undefined}
                    sx={{
                      bgcolor: allAnswered ? `${accentColor}20` : "grey.100",
                      color: allAnswered ? accentColor : "text.secondary",
                      fontWeight: 600,
                      border: allAnswered ? `1px solid ${accentColor}40` : "none",
                      "& .MuiChip-icon": { color: accentColor },
                    }}
                  />
                </Box>

                {/* Questions */}
                <Box sx={{ px: 3, py: 2 }}>
                  <Stack divider={<Divider sx={{ my: 2 }} />} spacing={0}>
                    {topic.questions.map((q: PeerReviewQuestion, qi: number) => {
                      const ans = answers[q.id];
                      const isAnswered =
                        ans?.likert_rating !== null && ans?.likert_rating !== undefined
                          ? true
                          : ans?.yes_no_answer !== null && ans?.yes_no_answer !== undefined;

                      return (
                        <Box key={q.id}>
                          <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                            <Box
                              sx={{
                                mt: "2px",
                                minWidth: 24,
                                height: 24,
                                borderRadius: "50%",
                                bgcolor: isAnswered ? accentColor : "grey.200",
                                color: isAnswered ? "#fff" : "text.secondary",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                transition: "all 0.2s",
                              }}
                            >
                              {qi + 1}
                            </Box>
                            <Typography variant="body2" fontWeight={500} color="text.primary">
                              {q.question_text}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.disabled"
                                ml={0.75}
                              >
                                (optional)
                              </Typography>
                            </Typography>
                          </Box>

                          <Box pl={4.5}>
                            {q.question_type === "yes_no" ? (
                              <YesNoButtons
                                value={ans?.yes_no_answer ?? null}
                                onChange={(v) => setYesNo(q.id, v)}
                                disabled={isReadOnly}
                              />
                            ) : (
                              <Box>
                                <LikertButtons
                                  scale={q.likert_scale ?? 5}
                                  value={
                                    ans?.likert_rating !== null &&
                                    ans?.likert_rating !== undefined
                                      ? Number(ans.likert_rating)
                                      : null
                                  }
                                  onChange={(v) => setLikertRating(q.id, v)}
                                  disabled={isReadOnly}
                                />
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mt={0.25}
                                  sx={{ width: (q.likert_scale ?? 5) * 48 - 8, maxWidth: "100%" }}
                                >
                                  <Typography variant="caption" color="text.primary">
                                    Poor
                                  </Typography>
                                  <Typography variant="caption" color="text.primary">
                                    Excellent
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Paper>
            );
          })}

          {/* ── Overall comment ──────────────────────────────────────────── */}
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "#f5f5f5",
                borderBottom: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Overall Comment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Optional — share any additional thoughts about this colleague
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <SkillDocumentCommentBox
                value={overallComment}
                onChange={setOverallComment}
                placeholder="Add an overall comment about your colleague (optional)…"
                disabled={isReadOnly}
                compact={true}
              />
            </Box>
          </Paper>

          {/* ── Action buttons ────────────────────────────────────────────── */}
          {!isReadOnly && (
            <Box
              display="flex"
              gap={2}
              justifyContent="flex-end"
              sx={{
                position: "sticky",
                bottom: 16,
                zIndex: 10,
                bgcolor: "background.paper",
                px: 2,
                py: 1.5,
                borderRadius: 3,
                boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography
                variant="caption"
                color="text.primary"
                alignSelf="center"
                sx={{ flex: 1 }}
              >
                {answeredCount} of {totalQuestions} questions answered
              </Typography>
              <Button
                variant="outlined"
                startIcon={savingDraft ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveDraft}
                disabled={savingDraft || submitting}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                onClick={() => setConfirmOpen(true)}
                disabled={savingDraft || submitting}
              >
                Submit Review
              </Button>
            </Box>
          )}
        </>
      )}

      {/* ── Submit confirmation dialog ───────────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Once submitted, your review cannot be edited or withdrawn.
          </Alert>
          <Typography>
            Submit your peer review for{" "}
            <strong>{assignment.reviewee_name}</strong>?
          </Typography>
          {answeredCount < totalQuestions && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {answeredCount} of {totalQuestions} questions answered — unanswered
              questions will be skipped.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default PeerReviewForm;
